import type { MentionableUserData } from '@jobilla/nexus-sdk'
import {
	type ChangeEvent,
	type FocusEvent,
	type KeyboardEvent,
	type TextareaHTMLAttributes,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { findMentionQuery, type NoteMention, sanitizeMentionName } from './note-mentions'

const MAX_SUGGESTIONS = 8

function trailingMentionStart(text: string): number | null {
	return findMentionQuery(text, text.length)?.start ?? null
}

function mentionLabel(user: MentionableUserData, users: MentionableUserData[]): string {
	const name = sanitizeMentionName(user.name) || sanitizeMentionName(user.email)
	const hasNamesake = users.some((other) => other.id !== user.id && sanitizeMentionName(other.name) === name)

	return hasNamesake ? sanitizeMentionName(`${name} <${user.email}>`) : name
}

interface MentionTextareaProps
	extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange' | 'onSelect'>
{
	value: string
	onChange: (value: string) => void
	mentions: NoteMention[]
	onMentionsChange: (mentions: NoteMention[]) => void
	users: MentionableUserData[]
	placement?: 'above' | 'below'
}

export function MentionTextarea({
	value,
	onChange,
	mentions,
	onMentionsChange,
	users,
	placement = 'above',
	onKeyDown,
	onFocus,
	onBlur,
	...textareaProps
}: MentionTextareaProps) {
	const { t } = useTranslation('common')
	const textareaRef = useRef<HTMLTextAreaElement>(null)
	const listRef = useRef<HTMLUListElement>(null)
	const emittedValue = useRef(value)
	const [caret, setCaret] = useState(value.length)
	const [isFocused, setIsFocused] = useState(false)
	const [highlightedIndex, setHighlightedIndex] = useState(0)
	const [dismissedStart, setDismissedStart] = useState<number | null>(() => trailingMentionStart(value))
	const [pendingCaret, setPendingCaret] = useState<number | null>(null)

	const mentionQuery = findMentionQuery(value, caret)
	const query = mentionQuery?.query ?? null

	const suggestions = useMemo(() => {
		if (query === null) {
			return []
		}
		const needle = query.toLowerCase()

		return users
			.filter((user) => user.name.toLowerCase().includes(needle) || user.email.toLowerCase().includes(needle))
			.slice(0, MAX_SUGGESTIONS)
	}, [query, users])

	const isOpen = isFocused
		&& mentionQuery !== null
		&& mentionQuery.start !== dismissedStart
		&& (suggestions.length > 0 || mentionQuery.query === '')
	const activeIndex = Math.min(highlightedIndex, Math.max(suggestions.length - 1, 0))

	useLayoutEffect(() => {
		setIsFocused(document.activeElement === textareaRef.current)
	}, [])

	useLayoutEffect(() => {
		if (pendingCaret === null || !textareaRef.current) {
			return
		}
		textareaRef.current.focus()
		textareaRef.current.setSelectionRange(pendingCaret, pendingCaret)
		setPendingCaret(null)
	}, [pendingCaret])

	useEffect(() => {
		if (value === emittedValue.current) {
			return
		}
		emittedValue.current = value
		setDismissedStart(trailingMentionStart(value))
		setCaret(value.length)
		setHighlightedIndex(0)
	}, [value])

	useEffect(() => {
		if (isOpen) {
			listRef.current?.scrollIntoView?.({ block: 'nearest' })
		}
	}, [isOpen])

	const emit = (nextValue: string) => {
		emittedValue.current = nextValue
		onChange(nextValue)
	}

	const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
		const nextValue = event.target.value
		const nextCaret = event.target.selectionStart ?? nextValue.length

		setCaret(nextCaret)
		setHighlightedIndex(0)
		if (findMentionQuery(nextValue, nextCaret) === null) {
			setDismissedStart(null)
		}
		emit(nextValue)
	}

	const pick = (user: MentionableUserData) => {
		if (!mentionQuery) {
			return
		}
		const name = mentionLabel(user, users)
		const inserted = `@${name} `
		const nextCaret = mentionQuery.start + inserted.length

		emit(value.slice(0, mentionQuery.start) + inserted + value.slice(caret))
		onMentionsChange([...mentions, { userId: user.id, name }])
		setDismissedStart(mentionQuery.start)
		setHighlightedIndex(0)
		setCaret(nextCaret)
		setPendingCaret(nextCaret)
	}

	const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
		if (isOpen && mentionQuery) {
			if (event.key === 'Escape') {
				event.preventDefault()
				event.stopPropagation()
				setDismissedStart(mentionQuery.start)
				return
			}

			if (suggestions.length > 0) {
				if (event.key === 'ArrowDown') {
					event.preventDefault()
					setHighlightedIndex((activeIndex + 1) % suggestions.length)
					return
				}
				if (event.key === 'ArrowUp') {
					event.preventDefault()
					setHighlightedIndex((activeIndex - 1 + suggestions.length) % suggestions.length)
					return
				}
				if (event.key === 'Enter' || event.key === 'Tab') {
					event.preventDefault()
					const highlighted = suggestions[activeIndex]
					if (highlighted) {
						pick(highlighted)
					}
					return
				}
			}
		}

		onKeyDown?.(event)
	}

	return (
		<div className="relative w-full">
			{isOpen && (
				<ul
					ref={listRef}
					role="listbox"
					aria-label={t('notes.mentionListLabel')}
					className={`absolute left-0 right-0 z-10 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg ${
						placement === 'above' ? 'bottom-full mb-2' : 'top-full mt-2'
					}`}
				>
					{suggestions.length === 0
						? <li className="px-3 py-2 text-sm text-gray-400">{t('notes.mentionEmpty')}</li>
						: suggestions.map((user, index) => (
							<li
								key={user.id}
								role="option"
								aria-selected={index === activeIndex}
								onMouseDown={(event) => {
									event.preventDefault()
									pick(user)
								}}
								onMouseEnter={() => setHighlightedIndex(index)}
								className={`cursor-pointer px-3 py-2 ${index === activeIndex ? 'bg-brand-50' : ''}`}
							>
								<span className="block text-sm text-gray-900">{user.name}</span>
								<span className="block text-xs text-gray-400">{user.email}</span>
							</li>
						))}
				</ul>
			)}
			<textarea
				{...textareaProps}
				ref={textareaRef}
				value={value}
				onChange={handleChange}
				onSelect={(event) => setCaret(event.currentTarget.selectionStart)}
				onKeyDown={handleKeyDown}
				onFocus={(event: FocusEvent<HTMLTextAreaElement>) => {
					setIsFocused(true)
					onFocus?.(event)
				}}
				onBlur={(event: FocusEvent<HTMLTextAreaElement>) => {
					setIsFocused(false)
					onBlur?.(event)
				}}
				aria-autocomplete="list"
				aria-expanded={isOpen}
			/>
		</div>
	)
}
